import { Injectable } from '@angular/core';
import { HttpDownloadProgressEvent, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { BaseApiService } from '@ep/shared';
import { BehaviorSubject, catchError, defer, EMPTY, Observable, tap } from 'rxjs';

const DEFAULT_API_URL = 'https://localhost:44323';

/** Per-widget load state the dashboard renders from: skeleton while loading, data once loaded. */
export interface WidgetState {
  status: 'loading' | 'loaded';
  data: unknown;
}

/** One streamed NDJSON line from the backend: a widget's type and its data payload. */
interface WidgetChunk {
  type: string;
  data: unknown;
}

@Injectable()
export class LoyaltyDashboardApiService extends BaseApiService {
  private readonly endpoint = '/v2/LoyaltyDashboard';

  private readonly _widgets$ = new BehaviorSubject<Record<string, WidgetState>>({});
  readonly widgets$: Observable<Record<string, WidgetState>> = this._widgets$.asObservable();

  /**
   * Open the single streaming request for the given widgets and update `widgets$` as each arrives.
   * The backend sends NDJSON (one widget per line, in request order); we flip each from loading to
   * loaded on arrival. `from`/`to` are the inclusive-start/exclusive-end bounds of the period window;
   * the backend scopes period widgets to [from, to). Subscribe for lifecycle — unsubscribing cancels
   * the in-flight request.
   */
  load(widgets: string[], from: Date, to: Date): Observable<WidgetChunk> {
    return defer(() => {
      this.initLoading(widgets);
      return this.streamChunks(widgets, from, to);
    }).pipe(
      tap((chunk) => this.applyChunk(chunk)),
      catchError((err) => {
        console.error(err);
        this.settlePending();
        return EMPTY;
      })
    );
  }

  /** Seed every requested widget as loading, so the dashboard shows skeletons immediately. */
  private initLoading(widgets: string[]): void {
    const state: Record<string, WidgetState> = {};
    for (const type of widgets) {
      state[type] = { status: 'loading', data: null };
    }
    this._widgets$.next(state);
  }

  private applyChunk(chunk: WidgetChunk): void {
    this._widgets$.next({
      ...this._widgets$.value,
      [chunk.type]: { status: 'loaded', data: chunk.data },
    });
  }

  /** On error/early close, flip any still-loading widget to loaded (empty) so skeletons don't hang. */
  private settlePending(): void {
    const current = { ...this._widgets$.value };
    let changed = false;
    for (const type of Object.keys(current)) {
      if (current[type].status === 'loading') {
        current[type] = { status: 'loaded', data: null };
        changed = true;
      }
    }
    if (changed) {
      this._widgets$.next(current);
    }
  }

  /**
   * Progressive read of the NDJSON body. Uses HttpClient (so auth/interceptors still apply) with
   * `responseType: 'text'` + `reportProgress`, parsing complete `\n`-delimited lines out of the
   * accumulated `partialText` as they arrive.
   */
  private streamChunks(widgets: string[], from: Date, to: Date): Observable<WidgetChunk> {
    const url = (this.configurationService.serviceApiBaseUrl ?? DEFAULT_API_URL) + this.endpoint;
    const body = { widgets, from: from.toISOString(), to: to.toISOString() };
    const request = new HttpRequest('POST', url, body, { responseType: 'text', reportProgress: true });

    return new Observable<WidgetChunk>((subscriber) => {
      let cursor = 0;

      const emitCompleteLines = (text: string) => {
        let newlineIndex: number;
        while ((newlineIndex = text.indexOf('\n', cursor)) !== -1) {
          const line = text.slice(cursor, newlineIndex).trim();
          cursor = newlineIndex + 1;
          if (line) {
            try {
              subscriber.next(JSON.parse(line) as WidgetChunk);
            } catch {
              /* ignore a malformed line (shouldn't happen — lines are whole by construction) */
            }
          }
        }
      };

      const subscription = this.http.request(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            emitCompleteLines((event as HttpDownloadProgressEvent).partialText ?? '');
          } else if (event.type === HttpEventType.Response) {
            emitCompleteLines((event as HttpResponse<string>).body ?? '');
            subscriber.complete();
          }
        },
        error: (err) => subscriber.error(err),
      });

      return () => subscription.unsubscribe();
    });
  }
}
